/**
 * What a PC's thread says beyond its messages: the time labels between
 * bursts, why a reply has not gone on, the message held for a PC that has
 * not picked up, and what a phone paired with the PC may do. Pure, so it is
 * tested.
 */

import type { Device, DeviceWait, Message } from '../api';

/** A label goes between two messages this far apart, or on another day. */
export const MARKER_GAP_S = 30 * 60;

interface Zone {
  /** A BCP 47 tag; the browser's own when left out. */
  locale?: string;
  /** An IANA zone; the browser's own when left out. */
  timeZone?: string;
}

/** `YYYY-MM-DD` of a moment in the zone. */
function dayOf(at: number, { timeZone }: Zone): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(at * 1000);
}

/** How many calendar days `at` is before `now`. */
function daysBetween(at: number, now: number, zone: Zone): number {
  const day = (s: string) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10));
  return Math.round((day(dayOf(now, zone)) - day(dayOf(at, zone))) / 86_400_000);
}

/**
 * The messages a time label goes in front of, with the moment it names: the
 * first, any that follows the one before by more than half an hour, and any
 * on a different day to it.
 */
export function timeMarks(messages: readonly Pick<Message, 'id' | 'createdAt'>[], zone: Zone = {}): Map<string, number> {
  const marks = new Map<string, number>();
  let before: number | null = null;
  for (const m of messages) {
    if (before === null || m.createdAt - before > MARKER_GAP_S || dayOf(m.createdAt, zone) !== dayOf(before, zone)) {
      marks.set(m.id, m.createdAt);
    }
    before = m.createdAt;
  }
  return marks;
}

/** A moment as a label: the time today, and the day too before that. */
export function markerLabel(at: number, now: number, zone: Zone = {}): string {
  const { locale, timeZone } = zone;
  // 9:05 PM, but 21:05 and 09:05 where the clock is 24-hour.
  const twelve = !!new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hour12;
  const time = new Intl.DateTimeFormat(locale, { hour: twelve ? 'numeric' : '2-digit', minute: '2-digit', timeZone }).format(at * 1000);
  const days = daysBetween(at, now, zone);
  if (days <= 0) return time;
  if (days === 1) return `Yesterday ${time}`;
  if (days < 7) return `${new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone }).format(at * 1000)} ${time}`;
  const sameYear = dayOf(at, zone).slice(0, 4) === dayOf(now, zone).slice(0, 4);
  const date = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }), timeZone }).format(at * 1000);
  return `${date} ${time}`;
}

/** Why a reply in a PC's thread has not gone on, in words; null when it
 *  waits on nothing the PC is to say. A reply marked offline whose PC is
 *  back is being woken. */
export function waitLine(wait: DeviceWait | undefined, pc: Pick<Device, 'name' | 'online'> | null): string | null {
  if (!wait) return null;
  const name = pc?.name ?? 'Your PC';
  if (wait.state === 'asking') return `Waiting for you at ${name}.`;
  return pc?.online ? `${name} is back. It picks this up now.` : `${name} is offline. Assist carries on when it's back.`;
}

/** What a message sent to a PC that has not picked up says of itself. */
export const heldLine = (name: string): string => `Sent. It goes in when ${name} picks up.`;

/** Whether a reply stopped to wait on the PC: the calls it left for the
 *  machine, or the reason the Worker gave. */
export function waitsOnPc(message: Message | undefined): boolean {
  if (message?.role !== 'assistant' || message.body.kind !== 'text') return false;
  return !!message.body.deviceWait || (message.body.pending ?? []).some((c) => c.where === 'desktop' && c.needs === 'result');
}

/** The messages held for the PC that are still held: only while the last
 *  reply waits on it. Once the PC has taken it on, the message went in with
 *  it, and the reply shows it. */
export function heldShown(held: readonly string[], messages: readonly Message[]): readonly string[] {
  return waitsOnPc(messages[messages.length - 1]) ? held : [];
}

/** Whether this phone may answer the PC's sheet the reply waits on: the PC
 *  made a request for it, and the phone holds a key for that PC. */
export function reviewable(message: Message | undefined, pcId: string | null, pairings: readonly string[] | null): boolean {
  if (!pcId || !pairings?.includes(pcId) || message?.role !== 'assistant' || message.body.kind !== 'text') return false;
  const wait = message.body.deviceWait;
  return wait?.state === 'asking' && !!wait.approval;
}

/** Whether to offer pairing this phone with the PC: an app that can pair,
 *  not paired with it yet. */
export const pairable = (pcId: string | null, pairings: readonly string[] | null): boolean => !!pcId && !!pairings && !pairings.includes(pcId);
